import { useTranslation } from 'react-i18next';
import { OlaggArticleBaseType, NrArticleBaseType } from '@olagg/db-types/newsroom';

export type NrAttributeWithLocale = 
  | 'title'
  | 'lead'
  | 'body'
  | 'coverImage';

export const useNrTranslation = () => {
  const { i18n: { language: lang } } = useTranslation(); 

  /**
   * Returns the appropiate data from a NR article depending on the current language
   * 
   * @param attribute NrAttributeWithLocale NR attribute name, to be extracted from the article
   * @param article OlaggArticleBaseType | NrArticleBaseType NR article to get the data from
   * @returns string
   */
  const tNr = (attribute: NrAttributeWithLocale, article: OlaggArticleBaseType | NrArticleBaseType): string => {
    let desiredOutput: string;
    let fallbackOutput: string;

    if (!article) return '';

    switch (attribute) {
      case 'title':
        desiredOutput = lang === 'es' ? article.title : (lang === 'en' ? ('titleEn' in article ? article.titleEn : article.title_en) : ('titlePt' in article ? article.titlePt : article.title_pt));
        fallbackOutput = article.title || ('titleEn' in article ? article.titleEn : article.title_en) || ('titlePt' in article ? article.titlePt : article.title_pt);
        break;
      case 'lead':
        desiredOutput = lang === 'es' ? article.lead : (lang === 'en' ? ('leadEn' in article ? article.leadEn : article.lead_en) : ('leadPt' in article ? article.leadPt : article.lead_pt));
        fallbackOutput = article.lead || ('leadEn' in article ? article.leadEn : article.lead_en) || ('leadPt' in article ? article.leadPt : article.lead_pt);
        break;
      case 'body':
        desiredOutput = lang === 'es' ? article.body : (lang === 'en' ? ('bodyEn' in article ? article.bodyEn : article.body_en) : ('bodyPt' in article ? article.bodyPt : article.body_pt));
        fallbackOutput = article.body || ('bodyEn' in article ? article.bodyEn : article.body_en) || ('bodyPt' in article ? article.bodyPt : article.body_pt);
        break;
      case 'coverImage':
        desiredOutput = lang === 'es' ? ('coverImage' in article ? article.coverImage?.original : article.cover_image?.original) : (lang === 'en' ? ('coverImage' in article ? article.coverImageEn?.original : article.cover_image_en?.original) : ('coverImage' in article ? article.coverImagePt?.original : article.cover_image_pt?.original));
        fallbackOutput = ('coverImage' in article ? article.coverImage?.original : article.cover_image?.original) || ('coverImage' in article ? article.coverImageEn?.original : article.cover_image_en?.original) || ('coverImage' in article ? article.coverImagePt?.original : article.cover_image_pt?.original);
        break;
      default:
        throw Error(`Invalid NR attribute, or attribute doesn't have locales: '${attribute}'`);
    }

    return desiredOutput || fallbackOutput || '';
  }

  /**
   * Returns the appropiate value depending on the current language
   * 
   * @param outputEs string Output if language is Spanish (es)
   * @param outputEn string Output if language is English (en)
   * @param outputPt string Output if language is Portuguese (pt)
   * @returns string
   */
  const tNrX = (outputEs: string, outputEn: string, outputPt: string): string => {
    return (lang === 'es'
      ? outputEs
      : (lang === 'en' ? outputEn : outputPt)
    ) || outputEs || outputEn || outputPt || '';
  }

  return {
    tNr,
    tNrX,
  };
}
